export interface ImageModifiers {
  format?: string;
  height?: number;
  width?: number;
  [key: string]: any;
}

export type ParamFormatter = (key: string, value: string) => string;

export type ParamMapper = { [key: string]: string } | ((key: string) => string);

export interface ProviderUrlBuilder {
  keyMap?: ParamMapper;
  formatter?: ParamFormatter;
  joinWith?: string;
  valueMap?: {
    [key: string]: ParamMapper;
  };
}

function createMapper(map: ParamMapper) {
  return (key: string) => {
    return map[key] || key;
  };
}

/**
 * Builds the parameterized Cloudinary url
 */
export function buildProviderUrl({
  formatter = (key, value) => `${key}=${value}`,
  keyMap,
  joinWith = '/',
  valueMap = {},
}: ProviderUrlBuilder = {}) {
  const keyMapper = typeof keyMap === 'function' ? keyMap : createMapper(keyMap || {});

  Object.keys(valueMap).forEach((valueKey) => {
    if (typeof valueMap[valueKey] !== 'function') {
      valueMap[valueKey] = createMapper(valueMap[valueKey]);
    }
  });

  return (modifiers: { [key: string]: string } = {}) => {
    const operations = Object.entries(modifiers).map(([key, value]) => {
      const mapper = valueMap[key];
      const newKey = keyMapper(key);
      let newVal = value;

      if (typeof mapper === 'function') {
        newVal = mapper(modifiers[key]);
      }

      return formatter(newKey, newVal);
    });

    return operations.join(joinWith);
  };
}

/**
 * Checks if a (sub)domain is included in a list of acceptable domains
 * @param str the (sub)domain to check
 * @param domains an array of valid domains
 */
export function isDomainValid(str = '', domains: string[] = []): boolean {
  const url = new URL(str);
  const host = url.host;

  return domains.some((domain) => {
    if (domain === host) {
      return true;
    }

    return domain.endsWith(`.${host}`);
  });
}
